
import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import { Video, Filter, FilterType, AiAnalysis, Kpi, AiAnalysisKpis, Company } from '../../types';
import { firestoreService } from '../../services/firestoreService';
import { AuthContext } from '../../contexts/AuthContext';
import Spinner from '../../components/common/Spinner';
import StatusBadge from '../../components/common/StatusBadge';
import KpiScoreChart from '../../components/common/KpiScoreChart';
import { generateBulkAnalysisPdf, generateAnalysisPdf } from '../../services/pdfService';
import AnalysisCharts, { AnalysisChartsRef } from '../../components/common/AnalysisCharts';
import { generateAggregatedAnalysis } from '../../services/geminiService';

// Componente para renderizar Markdown simple
const MarkdownLite: React.FC<{ text: string }> = ({ text }) => {
    const lines = text.split('\n');
    return (
        <div className="space-y-4">
            {lines.map((line, i) => {
                if (line.startsWith('# ')) return <h1 key={i} className="text-2xl font-bold text-blue-400 mt-6">{line.replace('# ', '')}</h1>;
                if (line.startsWith('## ')) return <h2 key={i} className="text-xl font-bold text-blue-300 mt-4">{line.replace('## ', '')}</h2>;
                if (line.startsWith('* **')) {
                    const parts = line.split('**:');
                    return (
                        <p key={i} className="ml-4 text-gray-300">
                            <span className="text-blue-200 font-bold">{parts[0].replace('* **', '')}:</span>
                            {parts[1]}
                        </p>
                    );
                }
                if (line.startsWith('* ')) return <li key={i} className="ml-6 text-gray-300 list-disc">{line.replace('* ', '')}</li>;
                return <p key={i} className="text-gray-400 leading-relaxed">{line}</p>;
            })}
        </div>
    );
};

const Accordion: React.FC<{ title: string; children: React.ReactNode; defaultOpen?: boolean }> = ({ title, children, defaultOpen = false }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    return (
        <div className="client-card overflow-hidden mt-4">
            <button onClick={() => setIsOpen(!isOpen)} className="w-full flex justify-between items-center p-4 hover:bg-white/5 transition-colors">
                <h4 className="font-bold text-white uppercase text-[10px] tracking-widest">{title}</h4>
                <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 text-gray-500 transform transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </button>
            <div className={`grid transition-all duration-500 ${isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
                <div className="overflow-hidden"><div className="p-6 bg-black/20 border-t border-white/5">{children}</div></div>
            </div>
        </div>
    );
};

const KpiItem: React.FC<{ title: string; content: string | Kpi }> = ({ title, content }) => {
    const isScoredKpi = typeof content === 'object' && content !== null && 'score' in content;
    const keyLabel = title.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    return (
        <div className="bg-black/20 p-4 rounded-xl flex items-center gap-4 border border-white/5">
            <div className="flex-1">
                <h3 className="font-bold text-xs text-gray-500 mb-1 uppercase tracking-tight">{keyLabel}</h3>
                <p className="text-gray-200 text-sm leading-relaxed">{isScoredKpi ? (content as Kpi).analysis : (content as string)}</p>
            </div>
            {isScoredKpi && <KpiScoreChart score={(content as Kpi).score} />}
        </div>
    );
};

const VideoAnalysisDisplay: React.FC<{ video: Video; analysis: AiAnalysis | null; company: Company | null; chartRef?: any }> = ({ video, analysis, company, chartRef }) => {
    const localChartRef = useRef<AnalysisChartsRef>(null);
    const setChartRef = (element: any) => {
        localChartRef.current = element;
        if (typeof chartRef === 'function') chartRef(element);
        else if (chartRef) chartRef.current = element;
    };

    const handleDownloadPdf = async () => {
        if (video && analysis) {
            const chartImages = localChartRef.current?.getChartImagesAsBase64();
            await generateAnalysisPdf(video, analysis, chartImages, company || undefined);
        }
    };

    return (
        <article className="client-card p-8 mb-10 border border-white/5 shadow-2xl animate-fade-in">
            <div className="flex flex-col lg:flex-row gap-10">
                <div className="lg:w-1/2">
                    <div className="flex justify-between items-start mb-4">
                        <h2 className="text-2xl font-bold text-white tracking-tight">{video.title}</h2>
                        <StatusBadge status={video.status} />
                    </div>
                    <p className="text-gray-400 text-sm mb-6 leading-relaxed">{video.description}</p>
                    <div className="bg-black rounded-2xl overflow-hidden aspect-video shadow-inner relative border border-white/5">
                        {video.storagePath ? (
                            <iframe src={video.storagePath.includes('drive.google.com') ? `https://drive.google.com/file/d/${video.storagePath.match(/d\/([a-zA-Z0-9_-]+)/)?.[1]}/preview` : video.storagePath} width="100%" height="100%" allowFullScreen className="border-0"></iframe>
                        ) : (
                            <img src={video.thumbnailUrl} alt="Thumbnail" className="w-full h-full object-cover opacity-30" />
                        )}
                    </div>
                </div>

                <div className="lg:w-1/2 flex flex-col">
                    <div className="flex justify-between items-center mb-6">
                        <h3 className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">Resultado de Análisis IA</h3>
                        {analysis && (
                            <button onClick={handleDownloadPdf} className="text-[10px] font-bold px-4 py-2 rounded-full bg-white/5 text-gray-400 hover:text-white transition-all flex items-center gap-2">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
                                PDF INDIVIDUAL
                            </button>
                        )}
                    </div>
                    {analysis && analysis.kpis ? (
                        <div className="flex-grow space-y-4">
                            <div className="bg-black/20 p-4 rounded-2xl border border-white/5">
                                <AnalysisCharts ref={setChartRef} kpis={analysis.kpis} />
                            </div>
                            <Accordion title="DESGLOSE DE CRITERIOS">
                                <div className="grid grid-cols-1 gap-4">
                                    {Object.keys(analysis.kpis).map(key => <KpiItem key={key} title={key} content={analysis.kpis[key]} />)}
                                </div>
                            </Accordion>
                        </div>
                    ) : (
                        <div className="flex-grow flex items-center justify-center bg-black/20 rounded-2xl border border-dashed border-white/10 p-10"><p className="text-gray-600 text-sm italic">Pendiente de auditoría...</p></div>
                    )}
                </div>
            </div>
        </article>
    );
};

const UserVideosPage: React.FC = () => {
  const [videos, setVideos] = useState<Video[]>([]);
  const [analyses, setAnalyses] = useState<Record<string, AiAnalysis>>({});
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [isAnalysisModalOpen, setIsAnalysisModalOpen] = useState(false);
  const [isGeneratingAnalysis, setIsGeneratingAnalysis] = useState(false);
  const [analysisResult, setAnalysisResult] = useState('');
  
  const chartRefs = useRef<Record<string, AnalysisChartsRef | null>>({});
  const authContext = useContext(AuthContext);
  const { tenantId, companyId } = authContext?.user || {};

  useEffect(() => {
    const fetchData = async () => {
      if (!tenantId || !companyId) { setLoading(false); return; }
      setLoading(true);
      try {
          const [v, c] = await Promise.all([firestoreService.getVideos(tenantId, companyId), firestoreService.getCompany(companyId)]);
          setVideos(v);
          setCompany(c || null);
          const analysisIds = v.map(video => video.aiAnalysisId).filter((id): id is string => id !== null);
          if (analysisIds.length > 0) {
              const res = await Promise.all(analysisIds.map(id => firestoreService.getAnalysis(id)));
              const map: Record<string, AiAnalysis> = {};
              res.forEach(a => { if (a) map[a.analysisId] = a; });
              setAnalyses(map);
          }
      } catch (e) { console.error(e); } finally { setLoading(false); }
    };
    fetchData();
  }, [tenantId, companyId]);

  const filteredVideos = videos.filter(v => v.title.toLowerCase().includes(searchTerm.toLowerCase()) || v.description.toLowerCase().includes(searchTerm.toLowerCase()));

  const handleRunGlobalAnalysis = async () => {
    if (videos.length === 0) return;
    setIsAnalysisModalOpen(true);
    setIsGeneratingAnalysis(true);
    try {
        const dataForAi = videos.map(v => ({ title: v.title, kpis: analyses[v.aiAnalysisId!]?.kpis })).filter(d => d.kpis);
        const result = await generateAggregatedAnalysis(dataForAi as any);
        setAnalysisResult(result);
    } catch (e) { setAnalysisResult("Error al generar el análisis."); } finally { setIsGeneratingAnalysis(false); }
  };

  if (loading) return <div className="flex flex-col items-center justify-center py-40"><Spinner size="lg" /><p className="mt-4 text-[10px] font-bold text-gray-600 uppercase tracking-widest animate-pulse">Sincronizando Workspace...</p></div>;

  return (
    <div className="container mx-auto pb-20">
      
      {videos.length > 0 ? (
          <>
            <div className="flex flex-col md:flex-row gap-6 mb-12 items-center justify-between sticky top-24 z-20 client-card p-6 bg-gray-900/80 backdrop-blur-md">
                <div className="relative flex-grow max-w-xl">
                    <input type="text" placeholder="Buscar reportes..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full px-5 py-3 bg-black/50 border border-white/5 rounded-2xl text-white outline-none focus:ring-2 focus:ring-blue-500 transition-all shadow-inner" />
                </div>
                <div className="flex gap-4">
                    <button onClick={handleRunGlobalAnalysis} className="px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-2xl font-bold text-[10px] uppercase tracking-widest shadow-lg shadow-orange-900/20 flex items-center gap-2 hover:scale-105 transition-all">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                        Análisis General con IA
                    </button>
                    <button onClick={() => generateBulkAnalysisPdf(filteredVideos, analyses, {}, company || undefined, analysisResult)} className="px-6 py-3 bg-purple-600 text-white rounded-2xl font-bold text-[10px] uppercase tracking-widest shadow-lg shadow-purple-900/20 hover:bg-purple-500 transition-all">
                        Bulk PDF Report
                    </button>
                </div>
            </div>

            <div className="space-y-12">
                {filteredVideos.map(v => (
                    <VideoAnalysisDisplay key={v.videoId} video={v} analysis={v.aiAnalysisId ? analyses[v.aiAnalysisId] : null} company={company} chartRef={(el: any) => { chartRefs.current[v.videoId] = el; }} />
                ))}
            </div>
          </>
      ) : (
          <div className="flex flex-col items-center justify-center py-40 text-center client-card p-12 bg-gray-900/20 border-dashed border-2 border-white/5">
             <div className="w-20 h-20 bg-blue-500/10 rounded-full flex items-center justify-center mb-6"><svg className="w-10 h-10 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"></path></svg></div>
             <h3 className="text-2xl font-bold text-white mb-2">Workspace Vacío</h3>
             <p className="text-gray-500 max-w-sm mx-auto">Aún no hay videos procesados en tu portal. Una vez que el analista suba los reportes, aparecerán aquí con su respectivo análisis de IA.</p>
          </div>
      )}

      {isAnalysisModalOpen && (
          <div className="fixed inset-0 bg-black/95 z-[100] flex items-center justify-center p-6 backdrop-blur-xl animate-fade-in">
              <div className="bg-gray-900 w-full max-w-5xl h-[85vh] rounded-3xl border border-white/10 shadow-2xl overflow-hidden flex flex-col">
                  <div className="p-8 border-b border-white/5 flex justify-between items-center bg-gray-900/50">
                      <div>
                          <h2 className="text-3xl font-bold text-white tracking-tight">Auditoría Ejecutiva Consolidada</h2>
                          <p className="text-gray-500 text-xs mt-1 uppercase tracking-widest font-bold">Procesado por Gemini Pro Vision</p>
                      </div>
                      <button onClick={() => setIsAnalysisModalOpen(false)} className="p-2 text-gray-400 hover:text-white transition-colors"><svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg></button>
                  </div>
                  <div className="flex-grow overflow-y-auto p-10 custom-scrollbar bg-black/20">
                      {isGeneratingAnalysis ? (
                          <div className="h-full flex flex-col items-center justify-center gap-6">
                              <Spinner size="lg" />
                              <div className="text-center">
                                  <p className="text-white font-bold text-xl mb-2">Analizando patrones en {videos.length} videos...</p>
                                  <p className="text-gray-500 text-sm italic">Gemini está identificando fortalezas y áreas de mejora sistémicas.</p>
                              </div>
                          </div>
                      ) : (
                          <div className="animate-fade-in">
                              <MarkdownLite text={analysisResult} />
                          </div>
                      )}
                  </div>
                  <div className="p-8 border-t border-white/5 flex justify-end gap-4 bg-gray-900/50">
                      <button onClick={() => setIsAnalysisModalOpen(false)} className="px-8 py-3 bg-white/5 text-white rounded-2xl font-bold hover:bg-white/10 transition-all">Cerrar Reporte</button>
                      {!isGeneratingAnalysis && (
                          <button onClick={() => window.print()} className="px-8 py-3 bg-blue-600 text-white rounded-2xl font-bold shadow-lg shadow-blue-900/20">Imprimir Pantalla</button>
                      )}
                  </div>
              </div>
          </div>
      )}
    </div>
  );
};

export default UserVideosPage;
